import { put, take, takeEvery } from 'redux-saga/effects';

import { logger } from '@/config/logger';
import { importSecrets, importVaultSecrets } from '@/modules/api/codegen';
import { actions as messagesActions } from '@/modules/messages';
import { actions as secretsActions } from '@/modules/secrets';

import { Fields } from '../../constants';
import {
    importRequest,
    importRequestFailure,
    importRequestSuccess,
    importValidationRequest,
    importValidationRequestCancel,
    importValidationRequestFailure,
    importValidationRequestSuccess,
    startImport,
} from '../actions';
import types from '../actionTypes';

function* importHandler({ payload, meta }: ReturnType<typeof startImport>) {
    try {
        // Validate import
        yield put(importValidationRequest(payload));

        const validationResult:
            | ReturnType<typeof importValidationRequestSuccess>
            | ReturnType<typeof importValidationRequestFailure>
            | ReturnType<typeof importValidationRequestCancel> = yield take([
            importValidationRequestSuccess.toString(),
            importValidationRequestFailure.toString(),
            importValidationRequestCancel.toString(),
        ]);

        switch (validationResult.type) {
            case types.IMPORT_VALIDATION_REQUEST_SUCCESS: {
                yield put(importRequest());

                // The vault import creates every row in that vault and has no replace mode.
                if (payload.vaultId) {
                    yield importVaultSecrets(
                        { vaultId: payload.vaultId },
                        {
                            [Fields.SECRETS]: payload.secrets,
                            [Fields.SECRETS_FORMAT]: payload.format,
                            [Fields.DELIMITER]: payload.delimiter,
                        },
                    );
                } else {
                    yield importSecrets({
                        [Fields.SECRETS]: payload.secrets,
                        [Fields.SECRETS_FORMAT]: payload.format,
                        [Fields.DELIMITER]: payload.delimiter,
                        [Fields.REPLACE_SECRETS]: JSON.stringify(payload.replaceSecrets),
                    });
                }

                yield put(importRequestSuccess());

                yield put(
                    messagesActions.displaySuccessMessage({
                        message: { id: 'success.import-export.import' },
                        options: {
                            duration: 5,
                        },
                    }),
                );

                yield put(secretsActions.syncSecrets(true));

                meta.success();

                break;
            }
            case types.IMPORT_VALIDATION_REQUEST_FAILURE: {
                if ((validationResult as ReturnType<typeof importValidationRequestFailure>).meta.isCsvValidationError) {
                    meta.failure(Fields.SECRETS, { message: 'import.form.error' });
                } else {
                    meta.failure();
                }

                break;
            }
            default: {
                meta.failure();
            }
        }
    } catch (e) {
        yield put(
            messagesActions.displayErrorMessage({
                message: { id: 'error.import-export.import' },
                options: {
                    duration: 5,
                },
            }),
        );

        yield put(importRequestFailure());

        logger.error(e);

        meta.failure();
    }
}

export default function* importSaga() {
    yield takeEvery(startImport.toString(), importHandler);
}
